
import { useState } from 'react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const [language, setLanguage] = useState('fr');

  const texts = {
    fr: {
      title: 'Wald Bladi',
      subtitle: 'Intérim & Emploi',
      description: 'Votre plateforme d\'entraide communautaire maghrébine pour l\'emploi et les services à Rouen.',
      quickLinks: 'Liens rapides',
      services: 'Services',
      contact: 'Contact',
      followUs: 'Suivez-nous',
      allRightsReserved: 'Tous droits réservés',
      privacyPolicy: 'Politique de confidentialité',
      termsOfService: 'Conditions d\'utilisation',
      links: {
        home: 'Accueil',
        jobs: 'Offres d\'emploi',
        candidates: 'Candidats',
        about: 'À propos',
        contact: 'Contact'
      },
      servicesList: {
        cleaning: 'Nettoyage',
        homeRepair: 'Réparation à domicile',
        elderCare: 'Aide aux personnes âgées',
        petCare: 'Garde d\'animaux',
        childCare: 'Garde d\'enfants',
        mechanics: 'Mécanique'
      },
      contactInfo: {
        address: '123 Rue de la République, 76000 Rouen',
        phone: '+33 2 35 XX XX XX',
        email: 'contact@waldbladi.fr'
      }
    },
    ar: {
      title: 'ولد بلادي',
      subtitle: 'العمل المؤقت والتوظيف',
      description: 'منصتكم للتضامن المجتمعي المغاربي للعمل والخدمات في روان.',
      quickLinks: 'روابط سريعة',
      services: 'الخدمات',
      contact: 'اتصل بنا',
      followUs: 'تابعونا',
      allRightsReserved: 'جميع الحقوق محفوظة',
      privacyPolicy: 'سياسة الخصوصية',
      termsOfService: 'شروط الخدمة',
      links: {
        home: 'الرئيسية',
        jobs: 'فرص العمل',
        candidates: 'المرشحون',
        about: 'من نحن',
        contact: 'اتصل بنا'
      },
      servicesList: {
        cleaning: 'التنظيف',
        homeRepair: 'الإصلاح المنزلي',
        elderCare: 'رعاية كبار السن',
        petCare: 'رعاية الحيوانات',
        childCare: 'رعاية الأطفال',
        mechanics: 'الميكانيكا'
      },
      contactInfo: {
        address: '123 شارع الجمهورية، 76000 روان',
        phone: '+33 2 35 XX XX XX',
        email: 'contact@waldbladi.fr'
      }
    }
  };

  const currentTexts = texts[language as keyof typeof texts];

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Language Switcher */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center bg-gray-800 rounded-lg p-1">
            <button
              onClick={() => setLanguage('fr')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                language === 'fr' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-300 hover:text-white'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLanguage('ar')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all cursor-pointer whitespace-nowrap ${
                language === 'ar' ? 'bg-emerald-600 text-white shadow-md' : 'text-gray-300 hover:text-white'
              }`}
            >
              العربية
            </button>
          </div>
        </div>

        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 ${language === 'ar' ? 'text-right' : ''}`}>
          {/* Brand */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-6">
              <div className="relative">
                {/* Icône principale avec effet néon */}
                <div className="w-16 h-16 bg-gradient-to-br from-purple-600 via-pink-600 to-red-600 rounded-xl flex items-center justify-center shadow-2xl border-2 border-white/20">
                  <i className="ri-community-line text-white text-3xl"></i>
                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent rounded-xl"></div>
                </div>
                {/* Badge étoile */}
                <div className="absolute -top-1 -right-1 w-6 h-6 bg-gradient-to-br from-yellow-400 to-orange-5   00 rounded-full flex items-center justify-center">
                  <i className="ri-star-fill text-white text-xs"></i>
                </div>
              </div>
              
              <div className="ml-4">
                <h3 className="flex items-center gap-3 mb-2">
                  <span 
                    className="text-4xl lg:text-5xl font-black bg-gradient-to-r from-purple-400 via-pink-400 to-red-400 bg-clip-text text-transparent"
                    style={{ fontFamily: 'Impact, Arial Black, sans-serif' }}
                  >
                    Wald Bladi
                  </span>
                  <span className="text-5xl lg:text-6xl font-black text-yellow-400">:</span>
                  <span 
                    className="text-3xl lg:text-4xl font-black bg-gradient-to-r from-emerald-300 via-teal-300 to-cyan-300 bg-clip-text text-transparent"
                    style={{ fontFamily: 'Arial Black, sans-serif' }}
                  >
                    ولد بلادي
                  </span>
                </h3>
                <p className="text-emerald-400 text-lg font-bold">{currentTexts.subtitle}</p>
              </div>
            </div>
            <p className="text-gray-300 text-sm leading-relaxed">
              {currentTexts.description}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{currentTexts.quickLinks}</h4>
            <ul className="space-y-2">
              {Object.entries(currentTexts.links).map(([key, value]) => (
                <li key={key}>
                  <Link
                    to={key === 'home' ? '/' : `/${key}`}
                    className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer"
                  >
                    {value}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{currentTexts.services}</h4>
            <ul className="space-y-2">
              {Object.entries(currentTexts.servicesList).map(([key, value]) => (
                <li key={key}>
                  <Link
                    to={`/services/${key}`}
                    className="text-gray-300 hover:text-emerald-400 transition-colors cursor-pointer"
                  >
                    {value}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">{currentTexts.contact}</h4>
            <div className="space-y-3">
              <div className="flex items-start">
                <i className="ri-map-pin-line text-emerald-400 mt-1 mr-3 flex-shrink-0"></i>
                <p className="text-gray-300 text-sm">{currentTexts.contactInfo.address}</p>
              </div>
              <div className="flex items-center">
                <i className="ri-phone-line text-emerald-400 mr-3 flex-shrink-0"></i>
                <p className="text-gray-300 text-sm">{currentTexts.contactInfo.phone}</p>
              </div>
              <div className="flex items-center">
                <i className="ri-mail-line text-emerald-400 mr-3 flex-shrink-0"></i>
                <p className="text-gray-300 text-sm">{currentTexts.contactInfo.email}</p>
              </div>
            </div>

            {/* Social Media */}
            <div className="mt-6">
              <h5 className="text-sm font-semibold mb-3">{currentTexts.followUs}</h5>
              <div className="flex space-x-3">
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-facebook-fill text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-sm"></i>
                </a>
                <a href="#" className="w-8 h-8 bg-gray-800 rounded-lg flex items-center justify-center hover:bg-emerald-600 transition-colors cursor-pointer">
                  <i className="ri-twitter-fill text-sm"></i>
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`border-t border-gray-800 mt-12 pt-8 flex flex-col md:flex-row justify-between items-center ${language === 'ar' ? 'text-right' : ''}`}>
          <p className="text-gray-400 text-sm mb-4 md:mb-0">
            © 2024 Wald Bladi. {currentTexts.allRightsReserved}.
          </p>
          <div className="flex space-x-6">
            <Link to="/privacy" className="text-gray-400 hover:text-emerald-400 text-sm transition-colors cursor-pointer">
              {currentTexts.privacyPolicy}
            </Link>
            <Link to="/terms" className="text-gray-400 hover:text-emerald-400 text-sm transition-colors cursor-pointer">
              {currentTexts.termsOfService}
            </Link>
            <a href="https://readdy.ai/?origin=logo" className="text-gray-400 hover:text-emerald-400 text-sm transition-colors cursor-pointer">
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
